/**
 * RCM (Reliability-Centered Maintenance) Decision Tree Engine
 * Implements SAE JA1011/JA1012, MSG-3 (aviation), and MIL-STD-2173 (military) RCM decision logic
 * for OptimalAvailability Studio
 *
 * Core concepts:
 * - Failure consequence classification (Safety, Operational, Non-Operational, Hidden)
 * - Task type recommendation (On-Condition, Scheduled Restoration, Scheduled Discard, etc.)
 * - Full decision tree traversal with question-answer flow
 * - Multi-standard support for aviation and military applications
 */

/**
 * RCM Standards enumeration
 */
const RCM_STANDARDS = {
  SAE_JA1011: {
    name: 'SAE JA1011',
    description: 'General RCM standard with Safety, Operational, Non-Operational, Hidden consequence classification',
    taskCategories: ['ON_CONDITION_SAFETY', 'SCHEDULED_RESTORATION', 'SCHEDULED_DISCARD', 'FAILURE_FINDING', 'RUN_TO_FAILURE']
  },
  MSG_3: {
    name: 'MSG-3',
    description: 'Aviation RCM standard for commercial aircraft maintenance',
    taskCategories: ['LUBRICATION_SERVICING', 'OPERATIONAL_VISUAL_CHECK', 'INSPECTION_FUNCTIONAL_CHECK', 'RESTORATION', 'DISCARD', 'COMBINATION']
  },
  MIL_STD_2173: {
    name: 'MIL-STD-2173',
    description: 'Military RCM standard with failure mode categorization',
    taskCategories: ['HARD_TIME', 'ON_CONDITION', 'CONDITION_MONITORING', 'FAILURE_FINDING', 'RUN_TO_FAILURE']
  }
};

/**
 * Represents a single node in the RCM decision tree
 */
class DecisionNode {
  constructor(id, question, yesId, noId, taskType = null) {
    this.id = id;
    this.question = question;
    this.yesId = yesId;
    this.noId = noId;
    this.taskType = taskType; // null for internal nodes, task type string for leaves
  }
}

/**
 * Main RCM Engine implementing SAE JA1011/JA1012, MSG-3, and MIL-STD-2173 decision logic
 */
export class RCMEngine {
  constructor() {
    this.decisionTree = this.buildDecisionTree();
    this.decisionHistory = [];
    this.standard = 'SAE_JA1011'; // Default standard
  }

  /**
   * Build the complete SAE JA1011 RCM decision tree
   * Returns map of nodeId -> DecisionNode
   */
  buildDecisionTree() {
    const tree = new Map();

    // Root question: Is the failure evident?
    tree.set('Q1', new DecisionNode(
      'Q1',
      'Is the failure evident to the operating crew under normal circumstances?',
      'Q2',
      'Q3'
    ));

    // YES path (evident failure)
    tree.set('Q2', new DecisionNode(
      'Q2',
      'Does the failure have a direct impact on safety?',
      'S1',
      'Q4'
    ));

    // Safety consequence branch
    tree.set('S1', new DecisionNode(
      'S1',
      null,
      null,
      null,
      'SAFETY_MAINTENANCE'
    ));

    // Environmental consequence check (Moubray RCM2 addition)
    tree.set('Q4E', new DecisionNode(
      'Q4E',
      'Does the failure cause a breach of any environmental standard or regulation?',
      'E1',
      'Q4'
    ));

    // Environmental consequence branch
    tree.set('E1', new DecisionNode(
      'E1',
      null,
      null,
      null,
      'ON_CONDITION_ENVIRONMENTAL'
    ));

    // Operational consequence check
    tree.set('Q4', new DecisionNode(
      'Q4',
      'Does the failure have a direct impact on operational capability?',
      'O1',
      'NO1'
    ));

    // Operational consequence branch
    tree.set('O1', new DecisionNode(
      'O1',
      null,
      null,
      null,
      'SCHEDULED_RESTORATION'
    ));

    // Non-operational consequence branch
    tree.set('NO1', new DecisionNode(
      'NO1',
      null,
      null,
      null,
      'SCHEDULED_DISCARD'
    ));

    // NO path (hidden failure)
    tree.set('Q3', new DecisionNode(
      'Q3',
      'Does the failure have a direct impact on safety?',
      'S2',
      'Q5'
    ));

    // Safety with hidden failure
    tree.set('S2', new DecisionNode(
      'S2',
      null,
      null,
      null,
      'FAILURE_FINDING'
    ));

    // Operational impact with hidden failure
    tree.set('Q5', new DecisionNode(
      'Q5',
      'Does the failure have a direct impact on operational capability?',
      'O2',
      'NO2'
    ));

    tree.set('O2', new DecisionNode(
      'O2',
      null,
      null,
      null,
      'FAILURE_FINDING'
    ));

    // Non-operational with hidden failure
    tree.set('NO2', new DecisionNode(
      'NO2',
      null,
      null,
      null,
      'RUN_TO_FAILURE'
    ));

    return tree;
  }

  /**
   * Classify failure consequence type based on failure mode characteristics
   * @param {Object} failureMode - {severity, occurrence, detection, isEvident, affectsSafety, affectsOperations}
   * @returns {string} - 'SAFETY', 'ENVIRONMENTAL', 'OPERATIONAL', 'NON_OPERATIONAL', or 'HIDDEN'
   */
  classifyConsequence(failureMode) {
    const { isEvident = true, affectsSafety = false, affectsEnvironment = false, affectsOperations = false } = failureMode;

    if (!isEvident) {
      return 'HIDDEN';
    }

    if (affectsSafety) {
      return 'SAFETY';
    }

    if (affectsEnvironment) {
      return 'ENVIRONMENTAL';
    }

    if (affectsOperations) {
      return 'OPERATIONAL';
    }

    return 'NON_OPERATIONAL';
  }

  /**
   * Calculate RPN (Risk Priority Number) for failure mode severity assessment
   * RPN = Severity × Occurrence × Detection
   * @param {Object} failureMode - {severity, occurrence, detection}
   * @returns {number} - RPN value (1-1000 scale)
   */
  calculateRPN(failureMode) {
    const severity = failureMode.severity || 1;
    const occurrence = failureMode.occurrence || 1;
    const detection = failureMode.detection || 1;
    return severity * occurrence * detection;
  }

  /**
   * Recommend maintenance task based on consequence type and failure mode
   * @param {Object} failureMode - failure mode object
   * @param {string} consequence - consequence classification
   * @returns {string} - recommended task type
   */
  recommendTask(failureMode, consequence) {
    const taskMap = {
      'SAFETY': 'ON_CONDITION_SAFETY',
      'ENVIRONMENTAL': 'ON_CONDITION_ENVIRONMENTAL',
      'OPERATIONAL': 'SCHEDULED_RESTORATION',
      'NON_OPERATIONAL': 'SCHEDULED_DISCARD',
      'HIDDEN': 'FAILURE_FINDING'
    };

    const baseTask = taskMap[consequence] || 'RUN_TO_FAILURE';

    // Adjust based on RPN if severity is high
    const rpn = this.calculateRPN(failureMode);
    if (rpn > 700 && consequence !== 'SAFETY') {
      return 'ON_CONDITION_PREDICTIVE';
    }

    return baseTask;
  }

  /**
   * Evaluate a single failure mode through complete RCM decision logic
   * @param {Object} failureMode - failure mode object with characteristics
   * @param {string} standard - RCM standard to apply: 'SAE_JA1011', 'MSG_3', or 'MIL_STD_2173' (default: SAE_JA1011)
   * @returns {Object} - decision result with path, task, and classification
   */
  evaluateFailureMode(failureMode, standard = 'SAE_JA1011') {
    const consequence = this.classifyConsequence(failureMode);
    const rpn = this.calculateRPN(failureMode);
    const decisionPath = this.getDecisionPath(failureMode);

    let task;
    let standardApplied;

    // Select logic based on standard parameter
    if (standard === 'MSG_3') {
      task = this.evaluateMSG3Decision(failureMode);
      standardApplied = 'MSG_3';
    } else if (standard === 'MIL_STD_2173') {
      task = this.evaluateMILSTD2173Decision(failureMode);
      standardApplied = 'MIL_STD_2173';
    } else {
      // Default to SAE JA1011
      task = this.recommendTask(failureMode, consequence);
      standardApplied = 'SAE_JA1011';
    }

    const decision = {
      failureModeId: failureMode.id,
      failureModeName: failureMode.name,
      consequence,
      recommendedTask: task,
      rpn,
      decisionPath,
      standardApplied,
      timestamp: new Date().toISOString()
    };

    this.decisionHistory.push(decision);
    return decision;
  }

  /**
   * Traverse decision tree based on yes/no answers
   * @param {Array<boolean>} answers - sequence of yes/no answers
   * @returns {Object} - final decision node and task recommendation
   */
  traverseDecisionTree(answers) {
    let currentNode = this.decisionTree.get('Q1');
    let currentNodeId = 'Q1';
    let answerIndex = 0;

    while (currentNode && !currentNode.taskType && answerIndex < answers.length) {
      const isYes = answers[answerIndex];
      currentNodeId = isYes ? currentNode.yesId : currentNode.noId;
      currentNode = this.decisionTree.get(currentNodeId);
      answerIndex++;
    }

    return {
      finalNodeId: currentNodeId,
      taskType: currentNode?.taskType || null,
      answerSequence: answers.slice(0, answerIndex)
    };
  }

  /**
   * Get the next question in the decision tree sequence
   * @param {string} currentNodeId - current node ID
   * @param {boolean} answer - yes or no to current question
   * @returns {Object} - next question node or null if at leaf
   */
  getNextQuestion(currentNodeId, answer) {
    const currentNode = this.decisionTree.get(currentNodeId);
    if (!currentNode) return null;

    const nextNodeId = answer ? currentNode.yesId : currentNode.noId;
    const nextNode = this.decisionTree.get(nextNodeId);

    return nextNode ? {
      nodeId: nextNodeId,
      question: nextNode.question,
      taskType: nextNode.taskType,
      isLeaf: nextNode.taskType !== null
    } : null;
  }

  /**
   * Get complete decision path from root to task recommendation
   * @param {Object} failureMode - failure mode object
   * @returns {Array<Object>} - path of decision nodes with questions
   */
  getDecisionPath(failureMode) {
    const path = [];
    let currentNode = this.decisionTree.get('Q1');
    let currentNodeId = 'Q1';

    // Simulate path based on consequence classification
    const consequence = this.classifyConsequence(failureMode);
    const isEvident = failureMode.isEvident !== false;

    // Q1: Is failure evident?
    path.push({
      nodeId: 'Q1',
      question: currentNode.question,
      answer: isEvident,
      order: 1
    });

    if (isEvident) {
      currentNode = this.decisionTree.get('Q2');
      const affectsSafety = failureMode.affectsSafety || false;

      path.push({
        nodeId: 'Q2',
        question: currentNode.question,
        answer: affectsSafety,
        order: 2
      });

      if (!affectsSafety) {
        // Environmental consequence check (Moubray RCM2)
        currentNode = this.decisionTree.get('Q4E');
        const affectsEnvironment = failureMode.affectsEnvironment || false;

        path.push({
          nodeId: 'Q4E',
          question: currentNode.question,
          answer: affectsEnvironment,
          order: 3
        });

        if (!affectsEnvironment) {
          currentNode = this.decisionTree.get('Q4');
          const affectsOperations = failureMode.affectsOperations || false;

          path.push({
            nodeId: 'Q4',
            question: currentNode.question,
            answer: affectsOperations,
            order: 4
          });
        }
      }
    } else {
      currentNode = this.decisionTree.get('Q3');
      const affectsSafety = failureMode.affectsSafety || false;

      path.push({
        nodeId: 'Q3',
        question: currentNode.question,
        answer: affectsSafety,
        order: 2
      });

      if (!affectsSafety) {
        currentNode = this.decisionTree.get('Q5');
        const affectsOperations = failureMode.affectsOperations || false;

        path.push({
          nodeId: 'Q5',
          question: currentNode.question,
          answer: affectsOperations,
          order: 3
        });
      }
    }

    return path;
  }

  /**
   * Process array of failure modes and return decisions for each
   * @param {Array<Object>} failureModes - array of failure mode objects
   * @param {string} standard - RCM standard to apply (default: SAE_JA1011)
   * @returns {Array<Object>} - array of decision results
   */
  evaluateAll(failureModes, standard = 'SAE_JA1011') {
    return failureModes.map(fm => this.evaluateFailureMode(fm, standard));
  }

  /**
   * MSG-3 Decision Logic for Aviation RCM
   * MSG-3 defines task categories based on failure effects and detectability
   * Category 1: Safety/reliability effects on aircraft → Hard-time or on-condition
   * Category 2: Operational effects → Condition monitoring preferred
   * Category 3: Economic effects → Cost-benefit decision
   *
   * @param {Object} cause - Failure cause object
   * @returns {string} - MSG-3 task category
   */
  evaluateMSG3Decision(cause) {
    const { affectsSafety = false, affectsOperations = false, isEvident = true, severity = 1 } = cause;

    // Category 1: Safety/reliability critical
    if (affectsSafety || severity >= 4) {
      // Safety-critical items need hard-time or on-condition monitoring
      return isEvident ? 'RESTORATION' : 'INSPECTION_FUNCTIONAL_CHECK';
    }

    // Category 2: Operational effects
    if (affectsOperations && isEvident) {
      // Operational items prefer condition monitoring or visual checks
      return 'OPERATIONAL_VISUAL_CHECK';
    }

    // Category 2: Operational but hidden
    if (affectsOperations && !isEvident) {
      return 'FAILURE_FINDING';
    }

    // Category 3: Economic effects
    // Economic items use cost-benefit analysis
    // If cost of failure is high and failure is evident, schedule maintenance
    if (cause.economicCriticality >= 3 && isEvident) {
      return 'LUBRICATION_SERVICING'; // Basic scheduled maintenance
    }

    // Default: Run to failure for low-criticality items
    return 'RUN_TO_FAILURE';
  }

  /**
   * MIL-STD-2173 Decision Logic for Military RCM
   * Uses failure mode categorization:
   * - Evident/Hidden × Safety/Operational/Economic
   * With specific decision paths for each combination
   *
   * @param {Object} cause - Failure cause object
   * @returns {string} - MIL-STD-2173 task recommendation
   */
  evaluateMILSTD2173Decision(cause) {
    const { isEvident = true, affectsSafety = false, affectsOperations = false, severity = 1 } = cause;

    // EVIDENT FAILURES
    if (isEvident) {
      // Evident + Safety-critical → Scheduled task mandatory (Hard-time or On-condition)
      if (affectsSafety || severity >= 3) {
        return 'HARD_TIME'; // Mandatory scheduled maintenance
      }

      // Evident + Operational → Cost justification check
      if (affectsOperations) {
        // If cost-justified, schedule maintenance; otherwise run to failure
        const costJustified = (cause.correctiveLaborCost || 0) + (cause.correctiveSpareCost || 0) >
                              (cause.preventiveLaborCost || 0) + (cause.preventiveSpareCost || 0);
        return costJustified ? 'ON_CONDITION' : 'RUN_TO_FAILURE';
      }

      // Evident + Economic only → Run to failure acceptable
      return 'RUN_TO_FAILURE';
    }

    // HIDDEN FAILURES
    else {
      // Hidden + Safety-critical → Failure-finding task required
      if (affectsSafety || severity >= 3) {
        return 'FAILURE_FINDING';
      }

      // Hidden + Operational → Condition monitoring preferred
      if (affectsOperations) {
        return 'CONDITION_MONITORING';
      }

      // Hidden + Economic only → Run to failure
      return 'RUN_TO_FAILURE';
    }
  }

  /**
   * Generate complete RCM worksheet data
   * @param {Array<Object>} failureModes - array of failure mode objects
   * @param {string} standard - RCM standard to apply (default: SAE_JA1011)
   * @returns {Object} - worksheet with decisions, statistics, and export data
   */
  generateRCMWorksheet(failureModes, standard = 'SAE_JA1011') {
    const decisions = this.evaluateAll(failureModes, standard);
    const taskDistribution = this.getTaskDistribution(decisions);
    const criticalItems = this.getCriticalItems(decisions, 300);
    const consequenceDistribution = this.getConsequenceDistribution(decisions);

    const standardInfo = RCM_STANDARDS[standard] || RCM_STANDARDS.SAE_JA1011;

    return {
      header: {
        title: 'RCM Analysis Worksheet',
        systemName: failureModes[0]?.systemName || 'System',
        analysisDate: new Date().toISOString(),
        standard: standardInfo.name,
        standardDescription: standardInfo.description
      },
      decisions,
      summary: {
        totalFailureModes: failureModes.length,
        totalDecisions: decisions.length,
        taskDistribution,
        consequenceDistribution,
        criticalItemCount: criticalItems.length,
        averageRPN: decisions.reduce((sum, d) => sum + d.rpn, 0) / decisions.length
      },
      criticalItems,
      exportData: {
        csvHeaders: [
          'Failure Mode ID',
          'Failure Mode Name',
          'Consequence Type',
          'Recommended Task',
          'Standard Applied',
          'RPN',
          'Decision Path Nodes'
        ],
        csvRows: decisions.map(d => [
          d.failureModeId,
          d.failureModeName,
          d.consequence,
          d.recommendedTask,
          d.standardApplied || standard,
          d.rpn,
          d.decisionPath.map(p => p.nodeId).join(' → ')
        ])
      }
    };
  }

  /**
   * Get distribution of task types across decisions
   * @param {Array<Object>} decisions - array of decision results
   * @returns {Object} - counts by task type
   */
  getTaskDistribution(decisions) {
    const distribution = {};

    decisions.forEach(decision => {
      const task = decision.recommendedTask;
      distribution[task] = (distribution[task] || 0) + 1;
    });

    return distribution;
  }

  /**
   * Get distribution of consequence types across decisions
   * @param {Array<Object>} decisions - array of decision results
   * @returns {Object} - counts by consequence type
   */
  getConsequenceDistribution(decisions) {
    const distribution = {};

    decisions.forEach(decision => {
      const consequence = decision.consequence;
      distribution[consequence] = (distribution[consequence] || 0) + 1;
    });

    return distribution;
  }

  /**
   * Filter and return high-criticality items based on RPN threshold
   * @param {Array<Object>} decisions - array of decision results
   * @param {number} threshold - RPN threshold value
   * @returns {Array<Object>} - decisions exceeding threshold, sorted by RPN descending
   */
  getCriticalItems(decisions, threshold = 300) {
    return decisions
      .filter(d => d.rpn >= threshold)
      .sort((a, b) => b.rpn - a.rpn);
  }

  /**
   * Get task recommendations summary with frequency and risk metrics
   * @returns {Object} - detailed task analysis
   */
  getTaskRecommendationSummary() {
    if (this.decisionHistory.length === 0) {
      return { message: 'No decisions evaluated yet' };
    }

    const summary = {};

    this.decisionHistory.forEach(decision => {
      const task = decision.recommendedTask;
      if (!summary[task]) {
        summary[task] = {
          count: 0,
          averageRPN: 0,
          totalRPN: 0,
          items: []
        };
      }
      summary[task].count++;
      summary[task].totalRPN += decision.rpn;
      summary[task].items.push({
        failureMode: decision.failureModeName,
        rpn: decision.rpn,
        consequence: decision.consequence
      });
    });

    // Calculate averages
    Object.keys(summary).forEach(task => {
      summary[task].averageRPN = Math.round(
        summary[task].totalRPN / summary[task].count
      );
    });

    return summary;
  }

  /**
   * Clear decision history
   */
  clearHistory() {
    this.decisionHistory = [];
  }

  /**
   * Get current decision history
   * @returns {Array<Object>} - all decisions made in current session
   */
  getDecisionHistory() {
    return [...this.decisionHistory];
  }

  /**
   * Validate failure mode object has required properties
   * @param {Object} failureMode - failure mode to validate
   * @returns {boolean} - true if valid
   */
  validateFailureMode(failureMode) {
    const required = ['id', 'name', 'severity', 'occurrence', 'detection'];
    return required.every(field => field in failureMode && failureMode[field] !== undefined);
  }

  /**
   * Get RCM decision tree structure for visualization
   * @returns {Object} - tree structure suitable for UI rendering
   */
  getTreeStructure() {
    return {
      nodes: Array.from(this.decisionTree.values()).map(node => ({
        id: node.id,
        question: node.question,
        taskType: node.taskType,
        isLeaf: node.taskType !== null
      })),
      edges: Array.from(this.decisionTree.values())
        .filter(node => node.yesId || node.noId)
        .flatMap(node => [
          { from: node.id, to: node.yesId, label: 'Yes' },
          { from: node.id, to: node.noId, label: 'No' }
        ])
    };
  }

  /**
   * OptimalRCM Integration - Cost fields for failure mode analysis
   * AWB 4.0 equivalence: OptimalRCM Predictions, Plots, Strategy
   */

  /**
   * Calculate cost impact for a failure mode
   */
  calculateCostImpact(failureMode) {
    const correctiveCost = (failureMode.correctiveLaborCost || 0) +
                           (failureMode.correctiveSpareCost || 0) +
                           (failureMode.correctiveConsequenceCost || 0);
    const preventiveCost = (failureMode.preventiveLaborCost || 0) +
                           (failureMode.preventiveSpareCost || 0);
    const downtimeCost = (failureMode.downtimeHours || 0) * (failureMode.downtimeCostPerHour || 500);
    const annualFailureRate = failureMode.failureRate || (1 / (failureMode.mtbf || 8760));

    const annualCorrectiveCost = correctiveCost * annualFailureRate;
    const annualPreventiveCost = preventiveCost * (failureMode.pmFrequency || 1);
    const annualDowntimeCost = downtimeCost * annualFailureRate;

    return {
      correctiveCost,
      preventiveCost,
      downtimeCost,
      annualCorrectiveCost,
      annualPreventiveCost,
      annualDowntimeCost,
      totalAnnualCost: annualCorrectiveCost + annualPreventiveCost + annualDowntimeCost,
      costBenefitRatio: annualPreventiveCost > 0
        ? (annualCorrectiveCost + annualDowntimeCost) / annualPreventiveCost
        : Infinity,
    };
  }

  /**
   * Cost-benefit analysis comparing RCM strategies
   * AWB 4.0: OptimalRCM Strategy report
   */
  compareStrategies(failureMode) {
    const runToFailure = this._calculateRTFCost(failureMode);
    const preventive = this._calculatePreventiveCost(failureMode);
    const predictive = this._calculatePredictiveCost(failureMode);
    const redesign = this._calculateRedesignCost(failureMode);

    const strategies = [
      { strategy: 'Run to Failure', ...runToFailure },
      { strategy: 'Preventive Maintenance', ...preventive },
      { strategy: 'Predictive/CBM', ...predictive },
      { strategy: 'Redesign', ...redesign },
    ];

    strategies.sort((a, b) => a.totalAnnualCost - b.totalAnnualCost);
    return {
      recommended: strategies[0].strategy,
      strategies,
      savings: strategies.length > 1 ? strategies[1].totalAnnualCost - strategies[0].totalAnnualCost : 0,
    };
  }

  _calculateRTFCost(fm) {
    const rate = fm.failureRate || (1 / (fm.mtbf || 8760));
    const repairCost = (fm.correctiveLaborCost || 200) + (fm.correctiveSpareCost || 500);
    const downtimeCost = (fm.downtimeHours || 8) * (fm.downtimeCostPerHour || 500);
    return {
      annualMaintenanceCost: 0,
      annualFailureCost: (repairCost + downtimeCost) * rate,
      totalAnnualCost: (repairCost + downtimeCost) * rate,
    };
  }

  _calculatePreventiveCost(fm) {
    const rate = fm.failureRate || (1 / (fm.mtbf || 8760));
    const pmInterval = fm.pmInterval || 2190; // quarterly default
    const pmCost = (fm.preventiveLaborCost || 100) + (fm.preventiveSpareCost || 200);
    const pmPerYear = 8760 / pmInterval;
    const residualRate = rate * 0.2; // 80% failure reduction with PM
    const repairCost = (fm.correctiveLaborCost || 200) + (fm.correctiveSpareCost || 500);
    const downtimeCost = (fm.downtimeHours || 8) * (fm.downtimeCostPerHour || 500);
    return {
      annualMaintenanceCost: pmCost * pmPerYear,
      annualFailureCost: (repairCost + downtimeCost) * residualRate,
      totalAnnualCost: pmCost * pmPerYear + (repairCost + downtimeCost) * residualRate,
    };
  }

  _calculatePredictiveCost(fm) {
    const rate = fm.failureRate || (1 / (fm.mtbf || 8760));
    const inspectionInterval = fm.inspectionInterval || 720; // monthly default
    const inspectionCost = fm.inspectionCost || 50;
    const pmCost = (fm.preventiveLaborCost || 100) + (fm.preventiveSpareCost || 200);
    const inspPerYear = 8760 / inspectionInterval;
    const residualRate = rate * 0.05; // 95% failure reduction with CBM
    const repairCost = (fm.correctiveLaborCost || 200) + (fm.correctiveSpareCost || 500);
    const downtimeCost = (fm.downtimeHours || 8) * (fm.downtimeCostPerHour || 500);
    return {
      annualMaintenanceCost: inspectionCost * inspPerYear + pmCost * rate * 0.8,
      annualFailureCost: (repairCost + downtimeCost) * residualRate,
      totalAnnualCost: inspectionCost * inspPerYear + pmCost * rate * 0.8 + (repairCost + downtimeCost) * residualRate,
    };
  }

  _calculateRedesignCost(fm) {
    const redesignCapex = fm.redesignCost || 50000;
    const designLife = fm.designLife || 10; // years
    const newRate = (fm.failureRate || (1 / (fm.mtbf || 8760))) * 0.01; // 99% reduction
    const repairCost = (fm.correctiveLaborCost || 200) + (fm.correctiveSpareCost || 500);
    return {
      annualMaintenanceCost: redesignCapex / designLife,
      annualFailureCost: repairCost * newRate,
      totalAnnualCost: redesignCapex / designLife + repairCost * newRate,
    };
  }

  /**
   * Generate OptimalRCM cause predictions
   * AWB 4.0: OptimalRCM Predictions/Cause Availability/Cost/Criticality
   */
  generateCausePredictions(failureModes) {
    return failureModes.map(fm => {
      const costs = this.calculateCostImpact(fm);
      const strategy = this.compareStrategies(fm);
      const rate = fm.failureRate || (1 / (fm.mtbf || 8760));
      const availability = 1 - (rate * (fm.downtimeHours || 8)) / 8760;

      return {
        id: fm.id,
        name: fm.name || fm.description,
        failureRate: rate,
        availability: Math.max(0, Math.min(1, availability)),
        rpn: (fm.severity || 1) * (fm.occurrence || 1) * (fm.detection || 1),
        annualCost: costs.totalAnnualCost,
        costBreakdown: costs,
        recommendedStrategy: strategy.recommended,
        potentialSavings: strategy.savings,
        criticality: {
          safety: fm.safetyCriticality || 0,
          environmental: fm.environmentalCriticality || 0,
          operational: fm.operationalCriticality || 0,
          cost: costs.totalAnnualCost,
        },
      };
    });
  }

  /**
   * Generate system-level OptimalRCM summary
   */
  generateSystemCostProfile(failureModes, missionTime = 8760) {
    const predictions = this.generateCausePredictions(failureModes);
    const intervals = 12; // Monthly profile
    const intervalLength = missionTime / intervals;

    const profile = [];
    let cumulativeCost = 0;

    for (let i = 0; i < intervals; i++) {
      const intervalCost = predictions.reduce((sum, p) => sum + p.annualCost / intervals, 0);
      cumulativeCost += intervalCost;
      profile.push({
        interval: i + 1,
        startTime: i * intervalLength,
        endTime: (i + 1) * intervalLength,
        intervalCost,
        cumulativeCost,
      });
    }

    return {
      totalAnnualCost: predictions.reduce((sum, p) => sum + p.annualCost, 0),
      topContributors: predictions.sort((a, b) => b.annualCost - a.annualCost).slice(0, 10),
      costProfile: profile,
      systemAvailability: predictions.reduce((prod, p) => prod * p.availability, 1),
    };
  }

  /**
   * Get RCM Standards reference data
   * @returns {Object} - All available RCM standards with descriptions and task categories
   */
  getRCMStandards() {
    return RCM_STANDARDS;
  }

  /**
   * Get supported RCM standards list
   * @returns {Array<string>} - Array of standard identifiers
   */
  getSupportedStandards() {
    return Object.keys(RCM_STANDARDS);
  }

  /**
   * Validate if a standard is supported
   * @param {string} standard - Standard identifier to validate
   * @returns {boolean} - True if standard is supported
   */
  isStandardSupported(standard) {
    return standard in RCM_STANDARDS;
  }

  /**
   * Set the default RCM standard for this engine instance
   * @param {string} standard - Standard identifier (SAE_JA1011, MSG_3, MIL_STD_2173)
   * @returns {boolean} - True if standard was set successfully
   */
  setDefaultStandard(standard) {
    if (this.isStandardSupported(standard)) {
      this.standard = standard;
      return true;
    }
    return false;
  }

  /**
   * Get current default RCM standard
   * @returns {string} - Current default standard identifier
   */
  getDefaultStandard() {
    return this.standard;
  }

  // ============================================================================
  // RCM2 MOUBRAY SEVEN-QUESTION PROCESS
  // ============================================================================

  /**
   * RCM2 Seven Questions (Moubray):
   * Q1: What are the functions and performance standards of the asset?
   * Q2: In what ways can it fail to fulfil its functions (functional failures)?
   * Q3: What causes each functional failure (failure modes)?
   * Q4: What happens when each failure occurs (failure effects)?
   * Q5: In what way does each failure matter (consequence classification)?
   * Q6: What should be done to predict or prevent each failure (proactive tasks)?
   * Q7: What should be done if a suitable proactive task cannot be found (default actions)?
   */
  static RCM2_QUESTIONS = [
    { id: 'Q1', title: 'Functions', question: 'What are the functions and associated desired standards of performance of the asset in its present operating context?', phase: 'analysis' },
    { id: 'Q2', title: 'Functional Failures', question: 'In what ways can it fail to fulfil its functions?', phase: 'analysis' },
    { id: 'Q3', title: 'Failure Modes', question: 'What causes each functional failure?', phase: 'analysis' },
    { id: 'Q4', title: 'Failure Effects', question: 'What happens when each failure occurs?', phase: 'analysis' },
    { id: 'Q5', title: 'Consequences', question: 'In what way does each failure matter?', phase: 'decision' },
    { id: 'Q6', title: 'Proactive Tasks', question: 'What should be done to predict or prevent each failure?', phase: 'decision' },
    { id: 'Q7', title: 'Default Actions', question: 'What should be done if a suitable proactive task cannot be found?', phase: 'decision' }
  ];

  /**
   * Consequence categories per Moubray RCM2 (5 categories including Environmental)
   */
  static CONSEQUENCE_CATEGORIES = [
    { id: 'hidden_safety', label: 'Hidden Safety', description: 'Hidden failure combined with associated multiple failure has safety or environmental consequences', color: '#DC2626' },
    { id: 'safety', label: 'Safety/Environmental (Evident)', description: 'Evident failure which could hurt or kill someone, or breach environmental standards', color: '#EF4444' },
    { id: 'environmental', label: 'Environmental', description: 'Failure causes a breach of environmental standards or regulations', color: '#10B981' },
    { id: 'operational', label: 'Operational', description: 'Evident failure which has a direct adverse effect on operational capability', color: '#3B82F6' },
    { id: 'non_operational', label: 'Non-Operational', description: 'Evident failure which does not affect operations but incurs repair cost', color: '#6B7280' },
    { id: 'hidden', label: 'Hidden (Non-Safety)', description: 'Hidden failure with no safety implications', color: '#F59E0B' }
  ];

  /**
   * Proactive task types per Moubray RCM2
   */
  static PROACTIVE_TASK_TYPES = [
    { id: 'on_condition', label: 'On-Condition Task', description: 'Scheduled task to detect a potential failure (predictive/condition monitoring)', applicable: 'When a clear P-F curve exists and the P-F interval is practical' },
    { id: 'scheduled_restoration', label: 'Scheduled Restoration', description: 'Scheduled task to restore item to initial capability at or before a specified age limit', applicable: 'When the failure mode has an age-related failure pattern and restoration is practical' },
    { id: 'scheduled_discard', label: 'Scheduled Discard', description: 'Scheduled task to discard item at or before a specified age limit', applicable: 'When the failure mode has an age-related failure pattern and restoration is not practical' },
    { id: 'failure_finding', label: 'Failure-Finding Task', description: 'Scheduled task to discover hidden failures', applicable: 'For hidden failures where no proactive task reduces risk to acceptable level' },
    { id: 'no_scheduled', label: 'No Scheduled Maintenance', description: 'Run to failure / no proactive task worth doing', applicable: 'When consequences do not justify the cost of any proactive task' },
    { id: 'redesign', label: 'Redesign', description: 'One-time change to design, operating procedures, or training', applicable: 'When no proactive task can reduce risk to acceptable level and consequences are unacceptable' }
  ];

  /**
   * Default action types per Moubray RCM2 (Q7)
   */
  static DEFAULT_ACTIONS = [
    { id: 'redesign_mandatory', label: 'Redesign Mandatory', description: 'Redesign is compulsory for safety/environmental hidden failures where no task reduces risk', severity: 'critical' },
    { id: 'redesign_desirable', label: 'Redesign Desirable', description: 'Redesign may be justified if the cost of failure consequences exceeds redesign cost', severity: 'high' },
    { id: 'no_scheduled_maintenance', label: 'No Scheduled Maintenance', description: 'Accept the risk of failure — no proactive task is worth doing', severity: 'low' },
    { id: 'combination', label: 'Combination of Tasks', description: 'No single task is effective but a combination of tasks may manage risk', severity: 'medium' },
    { id: 'change_procedures', label: 'Change Operating Procedures', description: 'Modify the way the asset is operated or maintained to reduce failure likelihood', severity: 'medium' },
    { id: 'training', label: 'Training', description: 'Operator or maintainer training to reduce human-caused failures', severity: 'low' }
  ];

  /**
   * Classify maintainable item for RCM2 Phase 0
   * @param {Object} item - equipment/asset item
   * @returns {Object} - classification result
   */
  classifyMaintainableItem(item) {
    const { safetyCriticality = 0, operationalCriticality = 0, environmentalCriticality = 0, failureHistory = [], replacementCost = 0 } = item;

    const totalCriticality = safetyCriticality + operationalCriticality + environmentalCriticality;
    const hasFrequentFailures = failureHistory.length >= 3;
    const isHighCost = replacementCost > 50000;

    if (safetyCriticality >= 4 || environmentalCriticality >= 4) {
      return { classification: 'critical', rcmRequired: true, reason: 'Safety or environmental criticality demands full RCM analysis' };
    }
    if (totalCriticality >= 8 || (hasFrequentFailures && operationalCriticality >= 3)) {
      return { classification: 'significant', rcmRequired: true, reason: 'High combined criticality or frequent failures with operational impact' };
    }
    if (totalCriticality >= 4 || isHighCost) {
      return { classification: 'economic', rcmRequired: true, reason: 'Economic significance warrants RCM analysis for cost optimisation' };
    }
    return { classification: 'run_to_failure', rcmRequired: false, reason: 'Low criticality — run to failure acceptable, no RCM analysis required' };
  }

  /**
   * Generate work instruction from RCM2 task recommendation
   * @param {Object} params - { failureMode, function, functionalFailure, consequence, taskType, equipment, interval }
   * @returns {Object} - structured work instruction
   */
  generateWorkInstruction(params) {
    const { failureMode, functionDesc, functionalFailure, consequence, taskType, equipment, interval, craft } = params;

    const TASK_PROCEDURES = {
      'on_condition': [
        'Perform visual inspection of component condition',
        'Take condition measurements (vibration/temperature/oil analysis as applicable)',
        'Compare readings against baseline and alert thresholds',
        'Record findings in condition monitoring database',
        'If potential failure detected, schedule corrective action within P-F interval'
      ],
      'scheduled_restoration': [
        'Isolate equipment and apply lockout/tagout procedures',
        'Remove and inspect components to be restored',
        'Clean, repair, or rebuild components to original specification',
        'Replace seals, gaskets, and consumable parts',
        'Reassemble and perform functional test',
        'Return to service and record restoration details'
      ],
      'scheduled_discard': [
        'Isolate equipment and apply lockout/tagout procedures',
        'Remove component scheduled for discard',
        'Install new replacement component per manufacturer specifications',
        'Perform functional test and verify performance standards',
        'Dispose of discarded component per waste management procedures',
        'Record replacement details and reset age counter'
      ],
      'failure_finding': [
        'Perform functional test of protective device/hidden function',
        'Verify device operates correctly when called upon',
        'Check alarm setpoints and trip values against design specification',
        'Record test results and compare with previous findings',
        'If failure found, initiate corrective action immediately'
      ],
      'redesign': [
        'Document current failure mode and consequences',
        'Evaluate design change options with engineering team',
        'Prepare modification specification and cost estimate',
        'Submit for engineering review and management approval',
        'Implement approved modification per management of change procedures',
        'Verify modification effectiveness through post-implementation review'
      ]
    };

    const procedures = TASK_PROCEDURES[taskType] || TASK_PROCEDURES['on_condition'];

    return {
      id: `WI-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      title: `${this._taskTypeLabel(taskType)} — ${equipment?.description || 'Equipment'}`,
      equipmentId: equipment?.id,
      equipmentName: equipment?.description || equipment?.id,
      functionDescription: functionDesc,
      functionalFailure: functionalFailure,
      failureMode: failureMode?.failureMode || failureMode?.description,
      consequenceCategory: consequence,
      taskType: taskType,
      taskTypeLabel: this._taskTypeLabel(taskType),
      interval: interval || 'As determined by P-F interval analysis',
      craft: craft || 'Mechanical',
      estimatedDuration: this._estimateDuration(taskType),
      safetyPrecautions: this._getSafetyPrecautions(taskType, consequence),
      procedures: procedures,
      toolsRequired: this._getToolsRequired(taskType),
      sparesRequired: failureMode?.sparesRequired || [],
      acceptanceCriteria: `Equipment meets performance standard: ${functionDesc || 'as specified'}`,
      generatedFrom: 'RCM2 Analysis',
      generatedDate: new Date().toISOString()
    };
  }

  _taskTypeLabel(type) {
    const labels = {
      'on_condition': 'On-Condition Task',
      'scheduled_restoration': 'Scheduled Restoration',
      'scheduled_discard': 'Scheduled Discard',
      'failure_finding': 'Failure-Finding Task',
      'no_scheduled': 'No Scheduled Maintenance',
      'redesign': 'Redesign Required',
      'ON_CONDITION_SAFETY': 'On-Condition (Safety)',
      'ON_CONDITION_ENVIRONMENTAL': 'On-Condition (Environmental)',
      'SCHEDULED_RESTORATION': 'Scheduled Restoration',
      'SCHEDULED_DISCARD': 'Scheduled Discard',
      'FAILURE_FINDING': 'Failure-Finding Task',
      'RUN_TO_FAILURE': 'Run to Failure'
    };
    return labels[type] || type;
  }

  _estimateDuration(taskType) {
    const durations = { 'on_condition': '1-2 hours', 'scheduled_restoration': '4-8 hours', 'scheduled_discard': '2-4 hours', 'failure_finding': '0.5-1 hour', 'redesign': 'Project-dependent' };
    return durations[taskType] || '2-4 hours';
  }

  _getSafetyPrecautions(taskType, consequence) {
    const base = ['Ensure equipment is isolated and locked out before work begins', 'Wear required PPE for the task'];
    if (consequence === 'safety' || consequence === 'hidden_safety') base.push('Additional safety observer required for safety-critical items');
    if (consequence === 'environmental') base.push('Ensure spill containment and environmental controls are in place');
    return base;
  }

  _getToolsRequired(taskType) {
    const tools = {
      'on_condition': ['Vibration analyser', 'Infrared thermometer', 'Oil sample kit', 'Data recorder'],
      'scheduled_restoration': ['Standard hand tools', 'Torque wrench', 'Lifting equipment as required', 'Alignment tools'],
      'scheduled_discard': ['Standard hand tools', 'New replacement component', 'Torque wrench'],
      'failure_finding': ['Functional test equipment', 'Calibrated instruments', 'Test procedures'],
      'redesign': ['Engineering drawings', 'Modification specification', 'As required per modification']
    };
    return tools[taskType] || ['Standard hand tools'];
  }

  /**
   * Package individual tasks into PM schedule groups by craft and interval
   * @param {Array} tasks - array of work instructions / task recommendations
   * @returns {Array} - grouped schedule entries
   */
  buildPMSchedule(tasks) {
    const groups = {};
    tasks.forEach(task => {
      const key = `${task.craft || 'General'}_${task.interval || 'Unscheduled'}`;
      if (!groups[key]) {
        groups[key] = {
          id: `PMG-${Object.keys(groups).length + 1}`,
          craft: task.craft || 'General',
          interval: task.interval || 'Unscheduled',
          tasks: [],
          totalEstimatedHours: 0
        };
      }
      groups[key].tasks.push(task);
      const hours = parseFloat(task.estimatedDuration) || 2;
      groups[key].totalEstimatedHours += hours;
    });
    return Object.values(groups).sort((a, b) => a.craft.localeCompare(b.craft));
  }
}
